(() => {
  if (customElements.get('ornamenta-company-form')) return;

  const instances = new Set();
  let state = null;
  let revision = 0;
  let savedRevision = 0;
  let pending = null;
  let updateUrl = '';
  const readyForms = new WeakSet();

  const renderAll = () => {
    document.documentElement.classList.toggle('ornamenta-company-active', Boolean(state && state.active));
    instances.forEach(instance => instance.render());
  };

  const attributes = () => {
    const result = { 'Ornamenta Business Purchase': state.active ? 'yes' : '' };
    Object.keys(state.values).forEach(key => {
      result[key] = state.active ? state.values[key].trim() : '';
    });
    return result;
  };

  const flush = () => {
    if (pending) return pending;
    if (savedRevision === revision) return Promise.resolve(true);
    pending = (async () => {
      try {
        while (savedRevision < revision) {
          const submittedRevision = revision;
          const controller = new AbortController();
          const timeout = setTimeout(() => controller.abort(), 15000);
          let response;
          try {
            response = await fetch(updateUrl, {
              method: 'POST',
              credentials: 'same-origin',
              headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
              body: JSON.stringify({ attributes: attributes() }),
              signal: controller.signal,
              keepalive: true
            });
          } finally {
            clearTimeout(timeout);
          }
          if (!response.ok) throw new Error('Cart attributes were not saved');
          await response.json();
          savedRevision = submittedRevision;
        }
        instances.forEach(instance => instance.showError(''));
        return true;
      } catch (error) {
        instances.forEach(instance => instance.showError(instance.dataset.saveError));
        return false;
      } finally {
        pending = null;
      }
    })();
    return pending;
  };

  class OrnamentaCompanyForm extends HTMLElement {
    connectedCallback() {
      this.checkbox = this.querySelector('[data-company-toggle]');
      this.fields = this.querySelector('[data-company-fields]');
      this.flag = this.querySelector('[data-company-flag]');
      this.controls = Array.from(this.querySelectorAll('[data-company-key]'));
      this.error = this.querySelector('[data-company-error]');
      if (!this.checkbox || !this.fields || !this.flag || !this.error) return;

      updateUrl = this.dataset.updateUrl;
      if (!state) {
        state = { active: this.checkbox.checked, values: {} };
        this.controls.forEach(control => { state.values[control.dataset.companyKey] = control.value; });
      }
      instances.add(this);
      this.abortController = new AbortController();
      const options = { signal: this.abortController.signal };
      this.addEventListener('input', event => this.onInput(event), options);
      this.addEventListener('change', event => this.onChange(event), options);
      renderAll();
    }

    disconnectedCallback() {
      instances.delete(this);
      if (this.abortController) this.abortController.abort();
    }

    onInput(event) {
      const control = event.target;
      const key = control.dataset.companyKey;
      if (!key || !state.active) return;
      if (state.values[key] !== control.value) {
        state.values[key] = control.value;
        revision += 1;
      }
      instances.forEach(instance => instance.showError(''));
      renderAll();
    }

    onChange(event) {
      if (event.target === this.checkbox) {
        state.active = this.checkbox.checked;
        revision += 1;
        instances.forEach(instance => instance.showError(''));
        renderAll();
        flush();
        return;
      }
      if (event.target.dataset.companyKey) {
        this.onInput(event);
        flush();
      }
    }

    render() {
      this.checkbox.checked = state.active;
      this.checkbox.setAttribute('aria-expanded', String(state.active));
      this.flag.value = state.active ? 'yes' : '';
      this.fields.hidden = !state.active;
      this.fields.disabled = !state.active;
      this.controls.forEach(control => {
        const value = state.values[control.dataset.companyKey] || '';
        if (control.value !== value) control.value = value;
        if (control.dataset.requiredCountries) {
          control.required = control.dataset.requiredCountries.split(',').includes(state.values['Company Country']);
          const mark = control.closest('label').querySelector('[data-required-mark]');
          if (mark) mark.hidden = !control.required;
        }
        control.setCustomValidity(state.active && control.required && !control.value.trim() ? this.dataset.requiredMessage : '');
      });
    }

    showError(message) {
      this.error.textContent = message || '';
      this.error.hidden = !message;
    }
  }

  customElements.define('ornamenta-company-form', OrnamentaCompanyForm);

  document.addEventListener('submit', async event => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.querySelector('ornamenta-company-form')) return;
    if (readyForms.has(form)) {
      readyForms.delete(form);
      return;
    }
    if (savedRevision === revision && !pending) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    const submitter = event.submitter;
    if (submitter && submitter.disabled) return;
    if (submitter) {
      submitter.disabled = true;
      submitter.setAttribute('aria-busy', 'true');
    }
    const success = await flush();
    if (submitter) {
      submitter.disabled = false;
      submitter.removeAttribute('aria-busy');
    }
    if (!success || !form.isConnected) return;
    readyForms.add(form);
    form.requestSubmit(submitter || undefined);
    readyForms.delete(form);
  }, true);

  document.addEventListener('click', async event => {
    if (!state || savedRevision === revision && !pending) return;
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = event.target.closest('a[href]');
    if (!link || link.target && link.target !== '_self') return;
    const url = new URL(link.href, window.location.href);
    if (url.origin !== window.location.origin || !/\/(cart|checkout)\/?$/.test(url.pathname)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (await flush()) window.location.assign(url.href);
  }, true);
})();
